import { useEffect, useRef, useCallback } from 'react';

import { addEvent, removeEvent } from '../helpers';
import { ESC_KEY } from '../constants/input.constants';

export function useDropdown(isOpen: boolean, onClose: () => void) {
    const dropdownRef = useRef<Element>(null);

    const handleClose = useCallback(
        (event) => {
            const current = dropdownRef.current;
            if (!current) {
                return;
            }

            const parent = current.parentNode;
            if (
                current.contains(event.target) ||
                parent?.contains(event.target)
            ) {
                return;
            }

            onClose();
        },
        [onClose],
    );

    const handleKeys = useCallback(
        (event) => {
            const key = event.which || event.keyCode;

            if (key === ESC_KEY) {
                onClose();
            }
        },
        [onClose],
    );

    const addEvents = useCallback(() => {
        addEvent(window, 'click', handleClose);
        addEvent(window, 'keydown', handleKeys);
    }, [handleClose, handleKeys]);

    const removeEvents = useCallback(() => {
        removeEvent(window, 'click', handleClose);
        removeEvent(window, 'keydown', handleKeys);
    }, [handleClose, handleKeys]);

    useEffect(() => {
        if (isOpen) {
            addEvents();
        } else {
            removeEvents();
        }

        return () => removeEvents();
    }, [isOpen, addEvents, removeEvents]);

    return dropdownRef;
}
